/**
 * Signalements: trust & safety (WALLIYO-0035). The contract for the console's
 * report screens, and for reporting someone and appealing a decision from the
 * app.
 *
 * - **A report is investigated.** Its `severity` decides how urgent it is;
 *   while it is investigated the reported person may be suspended, and a
 *   `verdict` follows: none, a warning, a temporary suspension, or a permanent
 *   ban.
 * - **Critical** means assault, sexual violence, serious theft, or threats
 *   with a weapon. The Owners are told, and it should be referred to the
 *   police. A critical report suspends the person automatically, except one
 *   from the app between two people who never had an introduction (no job
 *   that reached `accepted`, no conversation): that one goes to staff first,
 *   unsuspended, so one false report can't take anyone offline.
 * - **A suspension never ends by itself.** It lasts until a staff decision: a
 *   lift, a verdict, or an appeal decided in the person's favour. An interim
 *   suspension has a `reviewBy` deadline (INTERIM_SUSPENSION_REVIEW_RULE,
 *   72 hours by default); past it the suspension is late for staff, and still
 *   in force. Only a temporary-suspension verdict has an end date.
 * - **The suspended person can talk to the team.** A suspension, and an
 *   appeal, open a support case about the report in their name; it shows in
 *   their own cases in the app.
 * - **Suspensions and bans are customer features switched off** (ADR-0013
 *   §5), never a status column. What stays on is `RESTRICTION_ALLOWLIST`.
 * - **Nobody is quietly banned.** Every verdict has a written reason, the
 *   person is told, and they may appeal once. The appeal is decided by
 *   someone other than whoever issued the verdict.
 * - **Evidence is kept.** A legal hold freezes deletion of everything tied to
 *   the report. Police referrals, and every piece of data shared with the
 *   police, are recorded.
 *
 * Every staff write names the `version` the screen was showing (ADR-0016
 * §2.3), gives a reason, and takes an `Idempotency-Key` header. Every endpoint
 * answers under its own response name (WALLIYO-0068).
 */

import type { UserFeatureId } from "../actions/types";
import type { NotificationType } from "../notifications/notification-types";

// ── Vocabulary ───────────────────────────────────────────────────────────

export type SafetyReportSeverity = "low" | "medium" | "high" | "critical";

export type SafetyReportStatus = "open" | "investigating" | "decided" | "appealed" | "closed";

export type SafetyVerdict = "none" | "warning" | "temporary_suspension" | "permanent_ban";

/** What happened, in the reporter's words. The critical ones suspend automatically (see above). */
export type SafetyReportCategory =
  | "assault"
  | "sexual_violence"
  | "serious_theft"
  | "weapon_threat"
  | "theft"
  | "threats"
  | "harassment"
  | "fraud"
  | "unsafe_work"
  | "no_show"
  | "other";

/** How the report reached us: the app, a phone call, or staff noticing it themselves. */
export type SafetyReportSource = "app" | "phone" | "console";

export type SafetyAppealDecision = "upheld" | "overturned";

/** How a restriction came about. */
export type SafetyRestrictionKind = "interim_suspension" | "temporary_suspension" | "permanent_ban";

export type SafetyReportEntryType =
  | "report"
  | "evidence"
  | "severity_changed"
  | "suspended"
  | "suspension_lifted"
  | "verdict"
  | "appeal"
  | "appeal_decided"
  | "police_referral"
  | "police_data_shared"
  | "legal_hold_placed"
  | "legal_hold_released"
  | "linked"
  /** Critical, but not suspended automatically: the body says why. */
  | "suspension_withheld";

/** What a legal hold can freeze. A hold on a person covers their ID images and their account. */
export type LegalHoldEntityType = "safety_report" | "user" | "job" | "conversation";

/** What may be shared with the police, recorded category by category. */
export type PoliceDataCategory =
  | "identity"
  | "contact_details"
  | "id_document"
  | "messages"
  | "job_details"
  | "report_and_evidence"
  | "audit_log"
  | "payments";

export const SAFETY_REPORT_SEVERITIES: readonly SafetyReportSeverity[] = ["low", "medium", "high", "critical"];
export const SAFETY_REPORT_STATUSES: readonly SafetyReportStatus[] = [
  "open",
  "investigating",
  "decided",
  "appealed",
  "closed",
];
export const SAFETY_REPORT_CATEGORIES: readonly SafetyReportCategory[] = [
  "assault",
  "sexual_violence",
  "serious_theft",
  "weapon_threat",
  "theft",
  "threats",
  "harassment",
  "fraud",
  "unsafe_work",
  "no_show",
  "other",
];
export const POLICE_DATA_CATEGORIES: readonly PoliceDataCategory[] = [
  "identity",
  "contact_details",
  "id_document",
  "messages",
  "job_details",
  "report_and_evidence",
  "audit_log",
  "payments",
];

/**
 * The severity a report starts at, from its category. Staff can change it.
 * Assault, sexual violence, serious theft and threats with a weapon are
 * critical, and so suspend the person as soon as the report is made, unless
 * it comes from the app and the two people never had an introduction.
 */
export const DEFAULT_SEVERITY_BY_CATEGORY: Readonly<Record<SafetyReportCategory, SafetyReportSeverity>> = {
  assault: "critical",
  sexual_violence: "critical",
  serious_theft: "critical",
  weapon_threat: "critical",
  theft: "high",
  threats: "high",
  harassment: "medium",
  fraud: "medium",
  unsafe_work: "medium",
  no_show: "low",
  other: "low",
};

/**
 * What a suspended or banned person can still do: talk to support, and appeal.
 * Every other customer feature in SETTINGS is switched off for them, so a
 * feature added later is off too without anyone remembering to add it.
 * (Reading their existing threads, as evidence, isn't gated by a feature.)
 */
export const RESTRICTION_ALLOWLIST = [
  "CONTACT_US_USER_FEATURE_ENABLEMENT",
  "APPEAL_DECISION_USER_FEATURE_ENABLEMENT",
] as const satisfies readonly UserFeatureId[];

export const SAFETY_REPORTS_DEFAULT_LIMIT = 50;
export const SAFETY_REPORTS_MAX_LIMIT = 200;

// ── People ───────────────────────────────────────────────────────────────

export type SafetyPerson = { userId: string; name: string };
export type SafetyStaff = { staffMemberId: string; name: string };

// ── The list ─────────────────────────────────────────────────────────────

export type SafetyReportListItem = {
  safetyReportId: string;
  /** What people say on the phone, e.g. "S-0042". */
  reference: string;
  subject: SafetyPerson;
  /** Null when staff opened it without a customer reporter (a call from an unknown number). */
  reporter: SafetyPerson | null;
  openedBy: SafetyStaff | null;
  source: SafetyReportSource;
  category: SafetyReportCategory;
  severity: SafetyReportSeverity;
  status: SafetyReportStatus;
  verdict: SafetyVerdict | null;
  /** Whether the person is suspended or banned by this report right now. */
  restricted: boolean;
  /** An interim suspension from this report is in force and past its `reviewBy`: late for staff. */
  reviewOverdue: boolean;
  underLegalHold: boolean;
  /** Every report about the same person, this one included: a pattern shows here. */
  reportsAboutSubject: number;
  jobId: string | null;
  supportCaseId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type ListSafetyReportsResponse = { reports: SafetyReportListItem[] };

// ── One report ───────────────────────────────────────────────────────────

export type SafetyReportEntry = {
  safetyReportEntryId: string;
  type: SafetyReportEntryType;
  author:
    | { type: "staff"; staffMemberId: string; name: string }
    | { type: "customer"; userId: string; name: string }
    | { type: "system" };
  body: string;
  evidenceUrl: string | null;
  createdAt: string;
};

export type SafetyRestriction = {
  safetyRestrictionId: string;
  kind: SafetyRestrictionKind;
  /**
   * The end date of a temporary-suspension verdict. Null for everything else:
   * an interim suspension or a ban lasts until a staff decision.
   */
  expiresAt: string | null;
  /** An interim suspension's staff review deadline. Never an expiry. Null for a verdict. */
  reviewBy: string | null;
  /** In force and past `reviewBy`. */
  reviewOverdue: boolean;
  /** The customer features it switched off, e.g. QUOTE_JOBS_USER_FEATURE_ENABLEMENT. */
  switchedOff: UserFeatureId[];
  placedBy: SafetyStaff | null;
  placedAt: string;
  liftedAt: string | null;
  liftedBy: SafetyStaff | null;
  liftReason: string | null;
};

export type PoliceDataShare = {
  policeDataShareId: string;
  categories: PoliceDataCategory[];
  description: string;
  sharedBy: SafetyStaff;
  sharedAt: string;
};

export type PoliceReferral = {
  policeReferralId: string;
  station: string;
  officerName: string;
  officerContact: string | null;
  policeReference: string | null;
  referredBy: SafetyStaff;
  referredAt: string;
  dataShares: PoliceDataShare[];
};

export type LegalHold = {
  legalHoldId: string;
  entityType: LegalHoldEntityType;
  entityId: string;
  reason: string;
  placedBy: SafetyStaff;
  placedAt: string;
  releasedAt: string | null;
  releasedBy: SafetyStaff | null;
};

export type SafetyAppeal = {
  message: string;
  appealedAt: string;
  decision: SafetyAppealDecision | null;
  decisionReason: string | null;
  decidedBy: SafetyStaff | null;
  decidedAt: string | null;
};

export type SafetyReportDetail = SafetyReportListItem & {
  description: string;
  /** The reported person's own support case about this report, once they were suspended or appealed. */
  subjectSupportCaseId: string | null;
  verdictReason: string | null;
  decidedBy: SafetyStaff | null;
  decidedAt: string | null;
  appeal: SafetyAppeal | null;
  /** Critical, and not referred to the police yet. */
  needsPoliceReferral: boolean;
  /** The other reports about the same person, newest first. */
  relatedReports: Pick<
    SafetyReportListItem,
    "safetyReportId" | "reference" | "category" | "severity" | "status" | "verdict" | "createdAt"
  >[];
  restrictions: SafetyRestriction[];
  policeReferrals: PoliceReferral[];
  legalHolds: LegalHold[];
  entries: SafetyReportEntry[];
};

export type GetSafetyReportResponse = { safetyReport: SafetyReportDetail };

// ── Staff writes: every one answers with the report as it now is ─────────

type ReportResponse = { safetyReport: SafetyReportDetail };

/** POST /api/v1/console/safety/reports: staff open a report, e.g. from a phone call. */
export type OpenSafetyReportRequest = {
  subjectUserId: string;
  /** The customer who reported it, when known. */
  reporterUserId?: string | null;
  source: Exclude<SafetyReportSource, "app">;
  category: SafetyReportCategory;
  /** Defaults to the category's severity. */
  severity?: SafetyReportSeverity;
  description: string;
  jobId?: string | null;
  /** The support case this report came from. */
  supportCaseId?: string | null;
  reason?: string;
};
export type OpenSafetyReportResponse = ReportResponse;

export type AddReportEvidenceRequest = { body: string; evidenceUrl?: string | null; version: number; reason?: string };
export type AddReportEvidenceResponse = ReportResponse;

export type SetReportSeverityRequest = { severity: SafetyReportSeverity; version: number; reason: string };
export type SetReportSeverityResponse = ReportResponse;

export type SuspendPendingInvestigationRequest = { version: number; reason: string };
export type SuspendPendingInvestigationResponse = ReportResponse;

export type LiftSuspensionRequest = { version: number; reason: string };
export type LiftSuspensionResponse = ReportResponse;

/** A permanent ban is its own action (BAN_PERMANENTLY), not a verdict here. */
export type IssueVerdictRequest = {
  verdict: Exclude<SafetyVerdict, "permanent_ban">;
  /** Told to the person. Required: nobody is judged without a written reason. */
  verdictReason: string;
  /** Required for a temporary suspension. */
  suspendedUntil?: string | null;
  version: number;
  reason: string;
};
export type IssueVerdictResponse = ReportResponse;

export type BanPermanentlyRequest = { verdictReason: string; version: number; reason: string };
export type BanPermanentlyResponse = ReportResponse;

export type DecideAppealRequest = {
  decision: SafetyAppealDecision;
  /** Told to the person. */
  decisionReason: string;
  version: number;
  reason: string;
};
export type DecideAppealResponse = ReportResponse;

export type ReferToPoliceRequest = {
  station: string;
  officerName: string;
  officerContact?: string | null;
  policeReference?: string | null;
  version: number;
  reason: string;
};
export type ReferToPoliceResponse = ReportResponse;

export type ShareDataWithPoliceRequest = {
  categories: PoliceDataCategory[];
  /** Exactly what was handed over, e.g. "messages of 3–5 May, ID scan". */
  description: string;
  version: number;
  reason: string;
};
export type ShareDataWithPoliceResponse = ReportResponse;

export type PlaceLegalHoldRequest = { version: number; reason: string };
export type PlaceLegalHoldResponse = ReportResponse;

export type ReleaseLegalHoldRequest = { version: number; reason: string };
export type ReleaseLegalHoldResponse = ReportResponse;

// ── The app ──────────────────────────────────────────────────────────────

/** What the reporter sees of their report: never the investigation. */
export type CustomerSafetyReport = {
  safetyReportId: string;
  reference: string;
  category: SafetyReportCategory;
  createdAt: string;
};

/** POST /api/v1/safety/reports */
export type ReportUserRequest = {
  subjectUserId: string;
  category: SafetyReportCategory;
  description: string;
  /** The job it happened on; both people must be part of it. */
  jobId?: string | null;
};
export type ReportUserResponse = { report: CustomerSafetyReport };

/**
 * POST /api/v1/safety/reports/:safetyReportId/appeal: once, by the person the
 * verdict is about. The message also goes to their support case about the
 * report (opened if there is none), where the team replies.
 */
export type AppealDecisionRequest = { message: string };
export type AppealDecisionResponse = {
  appeal: { safetyReportId: string; reference: string; appealedAt: string; supportCaseId: string };
};

/**
 * Why the caller's account is paused, in words the app translates. Never the
 * report's details or who made it.
 *
 * - `under_review`: an interim suspension while a report is looked into.
 * - `suspended`: a temporary-suspension verdict.
 * - `banned`: a permanent ban.
 */
export type CustomerRestrictionReason = "under_review" | "suspended" | "banned";

export type CustomerAppealStatus = "none" | "pending" | "upheld" | "overturned";

export type CustomerRestriction = {
  reason: CustomerRestrictionReason;
  since: string;
  /** Only a temporary-suspension verdict ends on a date. Null: until the team decides, or for good. */
  until: string | null;
  /** The report the decision is on: what an appeal is sent to. */
  safetyReportId: string;
  reference: string;
  /** A verdict they haven't appealed yet (the allowlist keeps appealing on). */
  canAppeal: boolean;
  appealStatus: CustomerAppealStatus;
  /** Their open support case about it, to write to the team. */
  supportCase: { supportCaseId: string; reference: string } | null;
};

/**
 * GET /api/v1/me/restriction: whether the caller is suspended or banned now.
 * Null when they aren't. The app reads it at launch to show the one "compte
 * en pause" screen. A plain read, not audited.
 */
export type GetMyRestrictionResponse = { restriction: CustomerRestriction | null };

/** The verdicts a person may appeal, once each. */
export type AppealableVerdict = "warning" | "temporary_suspension" | "permanent_ban";

/**
 * A decision about the caller they may appeal, or did. A warning pauses
 * nothing, so it is never a restriction: this is where the app finds it.
 * Never the report's category, description, or who made it.
 */
export type CustomerDecision = {
  /** What an appeal is sent to: POST /api/v1/safety/reports/:safetyReportId/appeal. */
  safetyReportId: string;
  reference: string;
  verdict: AppealableVerdict;
  /** The reason the team gave them with the verdict (also in its notification). */
  verdictReason: string;
  decidedAt: string;
  /** A temporary suspension's end. Null for anything else. */
  suspendedUntil: string | null;
  /** Not appealed yet, and still decided: the appeal button. */
  canAppeal: boolean;
  appealStatus: CustomerAppealStatus;
  /** Their open support case about it, to write to the team. */
  supportCase: { supportCaseId: string; reference: string } | null;
};

/**
 * GET /api/v1/me/appealable-decisions: every warning, suspension or ban
 * verdict about the caller, newest first, with whether they can still appeal
 * it. The `safety_verdict` notification carries the same `safetyReportId`.
 * A plain read of the caller's own account: not audited.
 */
export type ListMyAppealableDecisionsResponse = { decisions: CustomerDecision[] };

/** The in-app notification types this sends. The client's never carries a reason. */
export const SAFETY_NOTIFICATION_TYPES = {
  /** To a client whose pro was suspended mid-introduction. */
  proUnavailable: "pro_unavailable",
  /** To the reported person: a verdict, with its reason, and how to appeal. */
  safetyVerdict: "safety_verdict",
  /**
   * To the reported person: they are suspended while a report is looked into,
   * until the team decides (`until` is null), with their support case about it.
   */
  safetySuspension: "safety_suspension",
  /** To the reported person: their appeal was decided. */
  safetyAppealDecided: "safety_appeal_decided",
} as const satisfies Record<string, NotificationType>;
